import { useMutation, useQuery, useQueryClient } from '@fv/client-core'
import type { LoadSaltResponse } from '@fv/models'

import { shipperFetch } from '@/utils/shipperFetch'

export const saltKeys = {
  all: ['salt'] as const,
  id: (loadId?: string | null) => [...saltKeys.all, loadId ?? null] as const,
}

const fetchSalt = (loadId?: string | null) => async () => {
  if (!loadId) throw new Error('`loadId` required to fetch salt')
  return shipperFetch<LoadSaltResponse | null>(`/shipments/${loadId}/salt`)
}

const postSaltRequest = (loadId: string) => async () => {
  if (!loadId) throw new Error('`loadId` required to request salt')
  return shipperFetch<LoadSaltResponse>(`/spot-quotes/${loadId}/salt-request`, {
    method: 'POST',
  })
}

const postSaltDismiss = (loadId: string) => async () => {
  if (!loadId) throw new Error('`loadId` required to dismiss salt')
  return shipperFetch<{ success: boolean }>(
    `/shipments/${loadId}/salt/dismiss`,
    {
      method: 'POST',
    },
  )
}

type UseSaltOpts = {
  enabled?: boolean
}

export function useSalt(loadId?: string | null, options?: UseSaltOpts) {
  return useQuery<LoadSaltResponse | null>(
    saltKeys.id(loadId),
    fetchSalt(loadId),
    {
      enabled: Boolean(loadId) && options?.enabled !== false,
    },
  )
}

export const useRequestSalt = (loadId?: string | null) => {
  const queryClient = useQueryClient()
  return useMutation(postSaltRequest(loadId), {
    onSuccess: data => {
      queryClient.setQueryData(saltKeys.id(loadId), data)
    },
  })
}

export const useDismissSalt = (loadId?: string | null) => {
  const queryClient = useQueryClient()
  return useMutation(postSaltDismiss(loadId), {
    onSuccess: () => {
      queryClient.setQueryData(saltKeys.id(loadId), null)
    },
  })
}
